import { useState } from 'react';
import { HeaderGlobalAction } from '@carbon/react';
import { Translate } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, setLanguage, type LanguageCode } from '@/i18n';

/**
 * LanguageSwitcher (A14) — a header action (globe/Translate icon) that opens a
 * small menu of supported languages and switches the whole UI live (no reload).
 * The choice is persisted per device.
 */
export const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = i18n.language as string;

  const choose = (code: LanguageCode) => {
    setLanguage(code);
    setOpen(false);
  };

  return (
    <div className="app-header__lang" style={{ position: 'relative' }}>
      <HeaderGlobalAction
        aria-label={t('header.changeLanguage')}
        isActive={open}
        onClick={() => setOpen((o) => !o)}
        tooltipAlignment="end"
        data-testid="language-switcher"
      >
        <Translate size={20} />
      </HeaderGlobalAction>

      {open && (
        <ul
          role="menu"
          data-testid="language-menu"
          style={{
            position: 'absolute',
            right: 0,
            top: '100%',
            zIndex: 9000,
            minWidth: '10rem',
            margin: 0,
            padding: '0.25rem 0',
            listStyle: 'none',
            background: 'var(--cds-layer-01)',
            border: '1px solid var(--cds-border-subtle-01)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
          }}
        >
          {SUPPORTED_LANGUAGES.map((l) => {
            const active = current === l.code || current.startsWith(`${l.code}-`);
            return (
              <li key={l.code}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  data-testid={`language-${l.code}`}
                  onClick={() => choose(l.code)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    cursor: 'pointer',
                    background: active ? 'var(--cds-layer-selected-01)' : 'transparent',
                    color: 'var(--cds-text-primary)',
                    fontWeight: active ? 600 : 400,
                  }}
                >
                  {l.label}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
